<script lang="ts">
  // Search (docs/spec.md, Recherche; maquette Search.dc.html): documents first, then the
  // messages grouped by conversation. The query and filters live in the URL.
  import { onMount, untrack } from 'svelte';
  import { api, ApiError, type DirectoryUser, type FileItem, type SearchResults } from '../lib/api';
  import { dayAndTime } from '../lib/format';
  import { filesPath, messagePath, router, searchPath, type SearchRoute } from '../lib/router.svelte';
  import { app } from '../lib/stores.svelte';
  import Avatar from './Avatar.svelte';

  let { route }: { route: SearchRoute } = $props();

  // svelte-ignore state_referenced_locally
  let text = $state(route.q);
  let results = $state<SearchResults | null>(null);
  let loading = $state(false);
  let error = $state('');
  let users = $state<DirectoryUser[]>([]);
  let input: HTMLInputElement | undefined = $state();

  const GROUPED = 3;
  const grouped = $derived(!route.conversation);

  onMount(() => {
    input?.focus();
    api.users().then((list) => (users = list.filter((u) => !u.is_bot))).catch(() => {});
  });

  function title(id: string): string {
    const c = app.conversation(id);
    if (!c) return 'Conversation';
    return c.kind === 'public' || c.kind === 'private' ? `# ${c.title}` : c.title;
  }

  const isoDay = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  function since(period: string | null): string | undefined {
    const now = new Date();
    if (period === '7' || period === '30') return isoDay(new Date(now.getTime() - (Number(period) - 1) * 86_400_000));
    if (period === 'year') return `${now.getFullYear()}-01-01`;
    return undefined;
  }

  function update(change: Partial<{ q: string; conversation: string | null; author: string | null; period: string | null }>) {
    router.go(
      searchPath({
        q: change.q ?? route.q,
        conversation: 'conversation' in change ? change.conversation : route.conversation,
        author: 'author' in change ? change.author : route.author,
        period: 'period' in change ? change.period : route.period,
      }),
      { replace: 'q' in change },
    );
  }

  // Back and forward: the field follows the URL.
  $effect(() => {
    const q = route.q;
    untrack(() => {
      if (text.trim() !== q) text = q;
    });
  });

  // Typing: the URL follows after a short pause, and the search with it.
  $effect(() => {
    const q = text.trim();
    if (q === route.q) return;
    const id = setTimeout(() => update({ q }), 350);
    return () => clearTimeout(id);
  });

  async function run(offset: number) {
    const q = route.q.trim();
    if (!q) {
      results = null;
      return;
    }
    loading = true;
    error = '';
    try {
      const page = await api.search({
        q,
        conversation_id: route.conversation ?? undefined,
        author_id: route.author ?? undefined,
        from: since(route.period),
        limit: grouped ? 50 : 20,
        offset,
      });
      results = offset > 0 && results ? { ...results, messages: [...results.messages, ...page.messages] } : page;
    } catch (e) {
      error = e instanceof ApiError ? e.message : 'La recherche a échoué';
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    void route.q;
    void route.conversation;
    void route.author;
    void route.period;
    void run(0);
  });

  /** Messages by conversation, in the order of their best result. */
  const groups = $derived.by(() => {
    if (!results) return [];
    const byId = new Map<string, SearchResults['messages']>();
    for (const r of results.messages) {
      const list = byId.get(r.message.conversation_id) ?? [];
      list.push(r);
      byId.set(r.message.conversation_id, list);
    }
    return [...byId.entries()].map(([id, items]) => ({
      id,
      items: grouped ? items.slice(0, GROUPED) : items,
      count: results!.conversations.find((c) => c.id === id)?.count ?? items.length,
    }));
  });
  const conversationsWithResults = $derived(
    route.conversation ? 1 : (results?.conversations.length ?? 0),
  );

  const summary = $derived.by(() => {
    if (!results) return '';
    const n = results.total;
    const head = `${n} résultat${n > 1 ? 's' : ''}`;
    const c = conversationsWithResults;
    return n > 0 ? `${head} dans ${c} conversation${c > 1 ? 's' : ''}` : head;
  });

  const ext = (f: FileItem) => (f.name.includes('.') ? (f.name.split('.').pop() ?? '').slice(0, 4).toUpperCase() : 'FICH');

  function close() {
    if (history.length > 1) history.back();
    else router.go('/');
  }
</script>

<section class="page" aria-labelledby="search-title">
  <h1 id="search-title" class="sr-only">Recherche</h1>
  <div class="column">
    <form
      role="search"
      onsubmit={(e) => {
        e.preventDefault();
        update({ q: text.trim() });
      }}
    >
      <div class="search-field">
        <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
        <label for="q" class="sr-only">Rechercher dans tous les messages</label>
        <input
          id="q"
          type="search"
          placeholder="Rechercher dans les messages et les documents"
          autocomplete="off"
          bind:this={input}
          bind:value={text}
          onkeydown={(e) => e.key === 'Escape' && close()}
        />
        <button type="button" class="esc" onclick={close} title="Fermer la recherche">Échap</button>
      </div>
      <div class="filters">
        <label for="f-conv" class="sr-only">Conversation</label>
        <select id="f-conv" value={route.conversation ?? ''} onchange={(e) => update({ conversation: e.currentTarget.value || null })}>
          <option value="">Toutes les conversations</option>
          {#each app.conversations as c (c.id)}
            <option value={c.id}>{title(c.id)}</option>
          {/each}
        </select>
        <label for="f-author" class="sr-only">Auteur</label>
        <select id="f-author" value={route.author ?? ''} onchange={(e) => update({ author: e.currentTarget.value || null })}>
          <option value="">Tous les auteurs</option>
          {#each users as u (u.id)}
            <option value={u.id}>{u.display_name}</option>
          {/each}
        </select>
        <label for="f-date" class="sr-only">Période</label>
        <select id="f-date" value={route.period ?? ''} onchange={(e) => update({ period: e.currentTarget.value || null })}>
          <option value="">Toutes les dates</option>
          <option value="7">7 derniers jours</option>
          <option value="30">30 derniers jours</option>
          <option value="year">Cette année</option>
        </select>
        <span class="spacer"></span>
        {#if results}
          <span class="summary" aria-live="polite">{summary}</span>
        {/if}
      </div>
    </form>

    {#if error}<p class="error" role="alert">{error}</p>{/if}

    {#if !route.q.trim()}
      <div class="hint">
        <p>Cherchez un mot dans les messages et les documents (.md, .txt) de vos conversations.</p>
        <ul>
          <li><code>certificat serveur</code> : les deux mots</li>
          <li><code>"mise en production"</code> : l'expression exacte</li>
          <li><code>certificat -wildcard</code> : sans le second mot</li>
          <li><code>recette or production</code> : l'un ou l'autre</li>
        </ul>
      </div>
    {:else if results}
      {#if results.files.length > 0}
        <section class="group" aria-labelledby="g-docs">
          <h2 id="g-docs">Documents<span class="n">· {results.files_total} résultat{results.files_total > 1 ? 's' : ''}</span></h2>
          {#each results.files as f (f.id)}
            <a
              class="row"
              href={filesPath(f.conversation_id, f.id)}
              onclick={(e) => {
                e.preventDefault();
                router.go(filesPath(f.conversation_id, f.id));
              }}
            >
              <span class="ext" aria-hidden="true">{ext(f)}</span>
              <span class="text">
                <span class="head"><span class="who">{f.name}</span><span class="when">{f.author.display_name} · {title(f.conversation_id)} · {dayAndTime(f.created_at)}</span></span>
                {#if f.headline}<span class="excerpt">… {@html f.headline} …</span>{/if}
              </span>
            </a>
          {/each}
          {#if results.files_total > results.files.length}
            <p class="more-note">{results.files_total - results.files.length} autres documents : précisez la recherche.</p>
          {/if}
        </section>
      {/if}

      {#each groups as g (g.id)}
        <section class="group" aria-labelledby="g-{g.id}">
          <h2 id="g-{g.id}">{title(g.id)}<span class="n">· {g.count} résultat{g.count > 1 ? 's' : ''}</span></h2>
          {#each g.items as r (r.message.id)}
            <a
              class="row"
              href={messagePath(g.id, r.message.id, r.message.root_id)}
              onclick={(e) => {
                e.preventDefault();
                router.go(messagePath(g.id, r.message.id, r.message.root_id));
              }}
            >
              <Avatar user={r.message.author} size={32} />
              <span class="text">
                <span class="head">
                  <span class="who">{r.message.author.display_name}</span>
                  <span class="when">{dayAndTime(r.message.created_at)}{r.message.root_id ? ' · dans un fil' : ''}</span>
                </span>
                <!-- Escaped by the server; only <mark> is kept. -->
                <span class="excerpt">{@html r.headline}</span>
              </span>
            </a>
          {/each}
          {#if grouped && g.count > g.items.length}
            <button type="button" class="see-more" onclick={() => update({ conversation: g.id })}>
              Voir {g.count - g.items.length === 1 ? "l'autre résultat" : `les ${g.count - g.items.length} autres résultats`}
            </button>
          {/if}
        </section>
      {/each}

      {#if !grouped && results.messages.length < results.total}
        <button type="button" class="btn load" disabled={loading} onclick={() => run(results!.messages.length)}>Afficher plus</button>
      {/if}
      {#if results.total === 0 && results.files.length === 0 && !loading}
        <p class="status">Aucun résultat pour « {route.q} ». Essayez un autre mot, ou retirez des filtres.</p>
      {/if}
    {:else if loading}
      <p class="status">Recherche…</p>
    {/if}
  </div>
</section>

<style>
  .page {
    height: 100%;
    overflow-y: auto;
    background: var(--bg-page);
  }
  .column {
    max-width: 880px;
    margin: 0 auto;
    padding: 32px 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .search-field {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 48px;
    padding: 0 8px 0 14px;
    border: 2px solid var(--accent);
    border-radius: 10px;
    background: var(--bg);
    color: var(--muted);
  }
  .search-field input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: none;
    background: transparent;
    font-size: 16px;
    color: var(--ink);
  }
  .esc {
    font-size: 12px;
    color: var(--muted);
    border: 1px solid var(--border);
    border-radius: 4px;
    padding: 0 6px;
    line-height: 20px;
    background: var(--bg);
  }
  .filters {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }
  select {
    height: 32px;
    max-width: 240px;
    padding: 0 10px;
    border: 1px solid var(--border-strong);
    border-radius: 16px;
    background: var(--bg);
    color: var(--ink);
  }
  .spacer {
    flex: 1;
  }
  .summary {
    font-size: 13px;
    color: var(--muted);
  }
  .group {
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 10px;
    overflow: hidden;
  }
  h2 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    font-size: 14px;
    font-weight: 600;
    border-bottom: 1px solid var(--border);
  }
  .n {
    font-weight: 400;
    color: var(--muted);
    font-size: 13px;
  }
  .row {
    display: flex;
    gap: 12px;
    padding: 12px 16px;
    color: var(--ink);
    text-decoration: none;
    border-bottom: 1px solid var(--border);
  }
  .row:last-child {
    border-bottom: 0;
  }
  .row:hover,
  .row:focus-visible {
    background: var(--bg-hover);
  }
  .text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .head {
    display: flex;
    gap: 8px;
    align-items: baseline;
    flex-wrap: wrap;
  }
  .who {
    font-weight: 600;
    overflow-wrap: anywhere;
  }
  .when {
    font-size: 12px;
    color: var(--muted);
  }
  .excerpt {
    overflow-wrap: anywhere;
  }
  .excerpt :global(mark) {
    background: var(--mark);
    color: var(--ink);
    border-radius: 3px;
    padding: 0 2px;
  }
  .ext {
    flex: 0 0 32px;
    height: 32px;
    border-radius: var(--radius-s);
    background: var(--status-doing-soft);
    color: var(--status-doing-text);
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .see-more {
    display: block;
    width: 100%;
    padding: 10px 16px;
    border: 0;
    border-top: 1px solid var(--border);
    background: transparent;
    color: var(--accent-text);
    font-weight: 600;
    text-align: left;
  }
  .see-more:hover {
    background: var(--bg-hover);
  }
  .more-note {
    margin: 0;
    padding: 10px 16px;
    font-size: 13px;
    color: var(--muted);
    border-top: 1px solid var(--border);
  }
  .load {
    align-self: center;
  }
  .status,
  .hint {
    color: var(--muted);
    margin: 0;
  }
  .hint ul {
    margin: 8px 0 0;
    padding-left: 18px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .hint code {
    font-family: var(--font-mono);
    font-size: 13px;
    color: var(--ink);
  }
  @media (max-width: 759px) {
    .column {
      padding: 16px 12px;
    }
    select {
      max-width: 100%;
      flex: 1 1 140px;
    }
  }
</style>
